import { Component, DoCheck, Input, OnInit, Optional } from '@angular/core';
import { Column, FilterOption, SmartTableService } from '../smart-table.service';
import { SmartTableConsumer } from '../smart-table-consumer';

@Component({
	selector: 'ws-smart-column-filter',
	templateUrl: './smart-column-filter.component.html',
	styleUrls: ['./smart-column-filter.component.scss'],
})
export class SmartColumnFilterComponent extends SmartTableConsumer implements OnInit, DoCheck {
	@Input() column!: Column;
	@Input() disabled = false;
	@Input() placeholder!: string;
	@Input() delay = 1500;
	@Input() mode = 'cell';
	@Input() maxlength = -1;

	public textValue: any;
	public selectValue: string = '';
	public selectOptions: FilterOption[] = [];

	private timeoutCall!: ReturnType<typeof setTimeout>;
	private _lastOptionsKey: string = '';

	constructor(@Optional() smartTableService: SmartTableService) {
		super(smartTableService);
	}

	ngOnInit() {
		this.initFromParams();
		this.updateSelectOptions();
		this.smartTableService.criteriaCleared$.subscribe(() => {
			this.textValue = undefined;
			this.selectValue = '';
		});
	}

	ngDoCheck() {
		if (this.filterType === 'select') {
			this.updateSelectOptions();
		}
	}

	get filterType(): string {
		return this.column?.filterType || 'string';
	}

	get filterOptions(): FilterOption[] {
		if (!this.column?.field) {
			return [];
		}
		return this.smartTableService.getFilterOptions(this.column.field);
	}

	updateSelectOptions() {
		const options = this.filterOptions;
		const optionsKey = options.map((o) => o?.value ?? 'null').join(',');
		if (optionsKey !== this._lastOptionsKey) {
			this._lastOptionsKey = optionsKey;
			this.selectOptions = [
				{ value: '', label: '_ws-framework.controls.no-filter' },
				...options.map((o) => {
					if (o?.value === '__NULL__') {
						return { value: '__NULL__', label: '_ws-framework.controls.unset' };
					}
					return { value: o.value, label: o.label || o.value };
				}),
			];
		}
	}

	get isSearchable(): boolean {
		return this.column?.searchable !== false;
	}

	onTextInput(event: any) {
		if (this.timeoutCall) {
			clearTimeout(this.timeoutCall);
		}
		this.timeoutCall = setTimeout(() => {
			this.emitTextFilter();
		}, this.delay);
	}

	onTextEnter() {
		this.emitTextFilter();
	}

	onSelectChange(value: string) {
		this.selectValue = value;
		this.emitSelectFilter();
	}

	private initFromParams() {
		const params = this.smartTableService.getParameters();

		if (params?.criteria) {
			const fields = Array.isArray(this.column?.field) ? this.column.field : [this.column?.field];
			const matchingFields: string[] = [];
			fields.forEach((f) => {
				matchingFields.push(f, '~' + f);
			});

			const criterion = params.criteria.find((c) => {
				return matchingFields.includes(c.filter!) && c.displayed;
			});
			if (criterion?.value) {
				if (this.filterType === 'select') {
					this.selectValue = criterion.value;
				} else {
					this.textValue = criterion.value;
				}
			}
		}
	}

	private emitTextFilter() {
		const fields = Array.isArray(this.column.field) ? this.column.field : [this.column.field];

		if (fields.length === 1) {
			this.smartTableService.columnFiltered({
				sortCols: [{ id: fields[0], dir: undefined, filterKey: this.textValue }],
			});
		} else {
			this.smartTableService.columnFiltered({
				sortCols: fields.map((f) => ({
					id: '~' + f,
					dir: undefined,
					filterKey: this.textValue,
				})),
			});
		}
	}

	private emitSelectFilter() {
		const fields = Array.isArray(this.column.field) ? this.column.field : [this.column.field];

		if (fields.length === 1) {
			this.smartTableService.columnFiltered({
				sortCols: [{ id: fields[0], dir: undefined, filterKey: this.selectValue }],
			});
		} else {
			// Remove any existing filters for this column (both ~ and non-~ variants)
			const allFieldVariants: string[] = [];
			fields.forEach((f) => allFieldVariants.push(f, '~' + f));
			this.smartTableService.columnFiltered(
				{ sortCols: allFieldVariants.map((id: string) => ({ id, dir: undefined, filterKey: '' })) },
				true
			);

			// Emit new filter with appropriate prefix
			const useOrFilter = this.selectValue !== '__NULL__' || this.column.nullFilterAsOr === true;
			this.smartTableService.columnFiltered({
				sortCols: fields.map((f) => ({
					id: useOrFilter ? '~' + f : f,
					dir: undefined,
					filterKey: this.selectValue,
				})),
			});
		}
	}
}
